import React, { useEffect, useState } from 'react';

import { Checkbox } from '@swyftx/aviary/atoms/Checkbox';
import { Label } from '@swyftx/aviary/atoms/Label';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ScrollArea } from '@swyftx/aviary/atoms/ScrollArea/ScrollArea';
import { Body, Utility } from '@swyftx/aviary/atoms/Typography';

import { api } from '@shared/api';

import { Controller, useForm } from 'react-hook-form';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector } from '../../xstate/LoanApplication.context';

interface FormData {
  declaration1: boolean;
  declaration2: boolean;
  declaration3: boolean;
}

const SignLoanAgreementStep: React.FC = () => {
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const {
    control,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      declaration1: false,
      declaration2: false,
      declaration3: false,
    },
  });

  const [agreementText, setAgreementText] = useState<string>('');

  useEffect(() => {
    api.endpoints
      .getLoanAgreement({
        data: {
          repaymentType: 'LINE_OF_CREDIT',
          // these values should be defined at this point
          loanOfferId: applicationData.loanOfferId ?? '',
          fiatPrincipalAmount: applicationData.borrowPrincipalAmount!.toNumber(),
          securityAmount: applicationData.securityAmount!.toNumber(),
          purposeDescription: applicationData.primaryPurpose ?? '',
        },
      })
      .then((result) => {
        if (result && typeof result === 'string') {
          setAgreementText(result);
        }
      });
  }, [
    applicationData.borrowPrincipalAmount,
    applicationData.loanOfferId,
    applicationData.primaryPurpose,
    applicationData.securityAmount,
  ]);

  return (
    <LoansStepContainer customTitle='Finalise application' submitDisabled={!isValid}>
      <main className='flex flex-col gap-32'>
        <Body>You must read and agree to the following to finalise your application.</Body>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            Loan agreement
          </Body>
          <ScrollArea className='h-[300px] min-h-0 w-full overflow-scroll rounded-md border p-4'>
            {agreementText || <span className='text-color-text-secondary'>Loading agreement...</span>}
          </ScrollArea>
          <Utility variant='caption'>
            Loans made by Web3 Loans Pty Ltd ACN 668 516 952 and managed by Web3 Ventures Pty Ltd trading as Block
            Earner (ACN 655 090 869) authorised representative 551024 of Mortgage Direct Pty Limited (ACN 075 721 434)
            (Australian Credit Licence 39187). Swyftx Pty Ltd [INSERT ACN] is a authorised credit representative [INSERT
            CREDIT REP NUMBER] of Mortgage Direct Pty Limited (ACN 075 721 434) (Australian Credit Licence 39187)
          </Utility>
        </div>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            By continuing I agree to the following
          </Body>
          <Controller
            name='declaration1'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FlexLayout direction='row' alignItems='start' spacing={8}>
                <Checkbox
                  className='h-20 w-20 border-2 border-color-border-main'
                  id='declaration-checkbox-1'
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor='declaration-checkbox-1'>I have read and understood the above loan agreement</Label>
              </FlexLayout>
            )}
          />
          {errors.declaration1 && <Utility className='text-color-text-error'>This field is required</Utility>}

          <Controller
            name='declaration2'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FlexLayout direction='row' alignItems='start' spacing={8}>
                <Checkbox
                  className='h-20 w-20 border-2 border-color-border-main'
                  id='declaration-checkbox-2'
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor='declaration-checkbox-2'>
                  I understand that cryptocurrency is a volatile and risky asset class. I understand that by entering
                  into this loan I may lose some or all of my provided cryptocurrency.
                </Label>
              </FlexLayout>
            )}
          />
          {errors.declaration2 && <Utility className='text-color-text-error'>This field is required</Utility>}

          <Controller
            name='declaration3'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FlexLayout direction='row' alignItems='start' spacing={8}>
                <Checkbox
                  className='h-20 w-20 border-2 border-color-border-main'
                  id='declaration-checkbox-3'
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor='declaration-checkbox-3'>
                  I agree that Swyftx may exchange my credit-related personal information with third party credit
                  reporting bodies, such as Experian or Equifax. I agree to the{' '}
                  <a href='#todo'>Credit Information Policy</a>.
                </Label>
              </FlexLayout>
            )}
          />
          {errors.declaration3 && <Utility className='text-color-text-error'>This field is required</Utility>}
        </div>
      </main>
    </LoansStepContainer>
  );
};

export { SignLoanAgreementStep };
